/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import { Amount, Contact, CorporatePass, PaymentStatus, ValidationError } from '@api';
import { Stripe } from '@stripe/stripe-js';
import classnames from 'classnames';
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { FormErrors, getFormSyncErrors, getFormValues, reduxForm, submit } from 'redux-form';
import { InjectedFormProps } from 'redux-form/lib/reduxForm';
import { scrollToValidation } from 'src/js/common/utils/DomUtils';
import { PaymentCompResolver } from 'src/js/enrol/containers/payment/components/PaymentCompResolver';
import { PaymentGateways } from 'src/js/model/checkout/payment/PaymentGateways';
import { IshState } from 'src/js/services/IshState';
import {
  changePhase,
  getAmount,
  setPayer,
  showSyncErrors,
  togglePayNowVisibility,
  updatePayNow,
} from '../../../actions/Actions';
import { Phase } from '../../../reducers/State';
import CheckoutService from '../../../services/CheckoutService';
import { setResultDetailsCorporatePass } from '../../summary/actions/Actions';
import {
  changeTab,
  checkoutCompleteWindcaveCcPayment,
  checkoutProcessEwayCCPayment,
  checkoutProcessSquareCcPayment,
  checkoutProcessStripeCCPayment,
  getCorporatePass, processPaymentV2,
  resetCorporatePass,
  setPaymentFetching,
  submitPaymentCorporatePass,
  updatePaymentStatus,
} from '../actions/Actions';
import { Tabs } from '../reducers/State';
import { CorporatePassFormValues, CreditCardFormValues, FieldName } from '../services/PaymentService';
import { Conditions } from './Conditions';
import CorporatePassComp from './CorporatePassComp';
import PayLaterComp from './PayLaterComp';

/**
 * @Deprecated will be remove, now it is used only as example
 */

interface Props extends InjectedFormProps {
  contacts: Contact[];
  amount: Amount;
  onSubmit: (data, dispatch, props) => any;
  payerId: string;
  voucherPayerEnabled: boolean;
  onSubmitPass?: (code: string) => any;
  corporatePass?: CorporatePass;
  corporatePassError?: string;
  fetching?: boolean;
  iframeUrl?: string;
  currentTab: Tabs;
  resetPass?: () => void;
  onSetPayer?: () => void;
  onAddPayer?: () => void;
  onAddCompany?: () => void;
  onChangeTab?: (tab) => void;
  onUnmountPassComponent?: () => void;
  corporatePassAvailable?: boolean;
  creditCardAvailable?: boolean;
  payLaterAvailable?: boolean;
  updatePayNow: (val, validate) => void;
  conditions?: {
    termsLabel?: string,
    termsUrl?: string,
  };
  paymentGatewayType?: PaymentGateways;
  formSyncErrors?: FormErrors<any>;
  dispatch?: Dispatch<any>;
  stateErrors?: ValidationError;
  formValues?: any;
  checkoutProcessSquareCcPayment: (creditCardToken: string, verificationToken: string) => void;
  checkoutProcessStripeCCPayment: (stripePaymentMethodId: string, stripe: Stripe) => void;
  checkoutProcessEwayCCPayment: (code: string) => void;
  checkoutCompleteWindcaveCcPayment: (sessionId: string) => void;
}

export const NAME = 'PaymentForm';

class PaymentForm extends React.Component<Props, any> {
  componentDidMount() {
    const { onChangeTab } = this.props;
    const validCurrentTab = this.getValidTab();
    onChangeTab(validCurrentTab);
  }

  getValidTab = () => {
    const { currentTab, creditCardAvailable, corporatePassAvailable } = this.props;

    if (currentTab === Tabs.payLater) {
      return Tabs.payLater;
    }

    if (currentTab === Tabs.corporatePass && corporatePassAvailable) {
      return Tabs.corporatePass;
    }

    if (creditCardAvailable) {
      return Tabs.creditCard;
    }

    return Tabs.none;
  };

  paymentTabOnClick = (e) => {
    e.preventDefault();
    const { currentTab, onChangeTab } = this.props;
    const nextTab = Number(e.target?.getAttribute('href')?.replace('#', ''));

    if (currentTab === nextTab) return;
    onChangeTab(nextTab);
  };

  render() {
    const {
      handleSubmit, contacts, amount, pristine, submitting, onSubmitPass, corporatePass, corporatePassError,
      onSetPayer, payerId, onAddPayer, onAddCompany, voucherPayerEnabled, currentTab, corporatePassAvailable, fetching,
      onUnmountPassComponent, conditions, creditCardAvailable, payLaterAvailable, updatePayNow,
      iframeUrl, checkoutProcessSquareCcPayment, checkoutProcessStripeCCPayment, checkoutProcessEwayCCPayment,
      checkoutCompleteWindcaveCcPayment, formSyncErrors, dispatch, formValues, paymentGatewayType
    } = this.props;

    const disabled = (pristine || submitting);

    const agreementChecked = formValues && formValues[FieldName.agreementFlag];

    return (
      <form onSubmit={handleSubmit} id="payment-form" className={classnames({ submitting: fetching || submitting })}>

        <Conditions conditions={conditions} agreementChecked={agreementChecked} />

        {agreementChecked && (Number(amount.ccPayment) !== 0 || (Number(amount.ccPayment) === 0 && corporatePass.id) || payLaterAvailable)
          && (
          <div>
            <div id="tabable-container">
              <PaymentFormNav
                paymentTabOnClick={this.paymentTabOnClick}
                currentTab={currentTab}
                corporatePassAvailable={corporatePassAvailable}
                creditCardAvailable={creditCardAvailable}
                payLaterAvailable={payLaterAvailable}
              />

              <div className="tab-content">
                {currentTab === Tabs.creditCard && creditCardAvailable
                  && (
                    <PaymentCompResolver
                      contacts={contacts}
                      amount={amount}
                      onSetPayer={onSetPayer}
                      onAddPayer={onAddPayer}
                      onAddCompany={onAddCompany}
                      payerId={payerId}
                      voucherPayerEnabled={voucherPayerEnabled}
                      disabled={disabled || !(formSyncErrors && Object.keys(formSyncErrors).length === 0)}
                      iframeUrl={iframeUrl}
                      checkoutProcessSquareCcPayment={checkoutProcessSquareCcPayment}
                      checkoutProcessStripeCCPayment={checkoutProcessStripeCCPayment}
                      checkoutProcessEwayCCPayment={checkoutProcessEwayCCPayment}
                      checkoutCompleteWindcaveCcPayment={checkoutCompleteWindcaveCcPayment}
                      dispatch={dispatch}
                      paymentGatewayType={paymentGatewayType}
                    />
                  )}

                {currentTab === Tabs.corporatePass && corporatePassAvailable
                  && (
                  <CorporatePassComp
                    onSubmitPass={onSubmitPass}
                    corporatePass={corporatePass}
                    onUnmount={onUnmountPassComponent}
                    corporatePassError={corporatePassError}
                    fetching={fetching}
                    passInputValue={formValues.code}
                  />
                  )}

                {currentTab === Tabs.payLater && payLaterAvailable
                  && (
                  <PayLaterComp
                    onInit={() => updatePayNow(0, false)}
                    contacts={contacts}
                    payerId={payerId}
                    onSetPayer={onSetPayer}
                    onAddPayer={onAddPayer}
                    onAddCompany={onAddCompany}
                    voucherPayerEnabled={voucherPayerEnabled}
                  />
                  )}

              </div>
            </div>
          </div>
          )}

        {agreementChecked && !(currentTab === Tabs.creditCard && creditCardAvailable && amount.ccPayment > 0)
          && (
          <div className="form-controls enrolmentsSelected">
            <input
              disabled={disabled}
              value="Confirm"
              className={classnames('btn btn-primary', { disabled })}
              id="paymentSubmit"
              name="paymentSubmit"
              type="submit"
            />
          </div>
          )}
      </form>
    );
  }
}

const PaymentFormNav = (props) => {
  const {
    paymentTabOnClick, currentTab, corporatePassAvailable, creditCardAvailable, payLaterAvailable
  } = props;

  return (
    <ul className="nav">
      {creditCardAvailable
        && (
        <li className={classnames('first', { active: currentTab === Tabs.creditCard })}>
          <a href={`#${Tabs.creditCard}`} onClick={paymentTabOnClick.bind(this)}>Credit card</a>
        </li>
        )}

      {corporatePassAvailable
        && (
        <li className={classnames({ active: currentTab === Tabs.corporatePass })}>
          <a href={`#${Tabs.corporatePass}`} onClick={paymentTabOnClick.bind(this)}>Corporate Pass</a>
        </li>
        )}

      {payLaterAvailable
        && (
        <li className={classnames({ active: currentTab === Tabs.payLater })}>
          <a href={`#${Tabs.payLater}`} onClick={paymentTabOnClick.bind(this)}>Pay later</a>
        </li>
        )}
    </ul>
  );
};

const validateCorporatePass = (data, props) => {
  const errors: any = {};

  if (!props.corporatePass.id || !data.corporatePass) {
    errors.corporatePass = '';
  }

  return errors;
};

const submitSuccessActions = [];

const Form = reduxForm({
  form: NAME,
  validate: (data:CorporatePassFormValues, props: Props) => {
    const errors = {};

    if (props.currentTab === Tabs.corporatePass) {
      return { ...errors, ...validateCorporatePass(data, props) };
    }

    return errors;
  },
  onSubmit: (data: CreditCardFormValues & CorporatePassFormValues, dispatch, props): void => {
    if (props.currentTab === Tabs.corporatePass && Number(props.amount.subTotal) !== 0) {
      dispatch(updatePaymentStatus({ status: PaymentStatus.IN_PROGRESS }));
      dispatch(submitPaymentCorporatePass(data));
      dispatch(setResultDetailsCorporatePass(props.corporatePass));
    } else {
      dispatch(setResultDetailsCorporatePass(null));
    }
  },
  onSubmitSuccess(result, dispatch, props) {
    submitSuccessActions.forEach(dispatch);
    while (submitSuccessActions.length > 0) {
      submitSuccessActions.pop();
    }

    if (props.currentTab === Tabs.creditCard && Number(props.amount.ccPayment) === 0) {
      dispatch(setPaymentFetching(true))
      dispatch(processPaymentV2())
    }
  },
  onSubmitFail: (errors, dispatch, submitError) => {
    if (errors && !submitError) {
      dispatch(showSyncErrors(errors));
    }
    dispatch(setPaymentFetching(false));
    setTimeout(() => scrollToValidation(), 50);
  },
})(PaymentForm);

const mapStateToProps = (state: IshState) => {
  const corporatePassError = state.checkout.error
    && state.checkout.error.fieldsErrors && state.checkout.error.fieldsErrors.find((er) => er.name === 'code');
  return {
    contacts: Object.values(state?.checkout?.contacts?.entities?.contact || {}),
    amount: state.checkout.amount,
    payerId: state.checkout.payerId,
    voucherPayerEnabled: CheckoutService.hasActiveVoucherPayer(state.checkout),
    corporatePass: state.checkout.payment.corporatePass,
    corporatePassError: corporatePassError && corporatePassError.error,
    currentTab: state.checkout.payment.currentTab,
    iframeUrl: state.checkout.payment.iframeUrl,
    fetching: state.checkout.payment.fetching,
    corporatePassAvailable: state.preferences.hasOwnProperty('corporatePassEnabled') ? state.preferences.corporatePassEnabled : true,
    creditCardAvailable: state.preferences.hasOwnProperty('creditCardEnabled') ? state.preferences.creditCardEnabled : true,
    payLaterAvailable: state.checkout.amount.isEditable && Number(state.checkout.amount.minPayNow) === 0,
    conditions: {
      termsUrl: state.preferences.termsUrl,
      termsLabel: state.preferences.termsLabel
    },
    paymentGatewayType: state.preferences.paymentGatewayType,
    formSyncErrors: getFormSyncErrors(NAME)(state),
    stateErrors: state.checkout.error,
    formValues: getFormValues(NAME)(state)
  };
};

const mapDispatchToProps = (dispatch) => ({
  onSetPayer: (id) => {
    dispatch(setPayer(id));
  },
  onAddPayer: () => dispatch(changePhase(Phase.AddContactAsPayer)),
  onAddCompany: () => dispatch(changePhase(Phase.AddContactAsCompany)),
  onSubmitPass: (code) => dispatch(getCorporatePass(code)),
  onChangeTab: (tab) => {
    dispatch(changeTab(tab));
  },
  onUnmountPassComponent: () => {
    dispatch(resetCorporatePass());
    dispatch(togglePayNowVisibility(true));
    dispatch(getAmount());
  },
  updatePayNow: (val, validate) => {
    dispatch(updatePayNow(val, validate));
  },
  checkoutCompleteWindcaveCcPayment: (sessionId: string) => {
    dispatch(checkoutCompleteWindcaveCcPayment(sessionId));
  },
  checkoutProcessSquareCcPayment: (creditCardToken: string, verificationToken: string) => {
    submitSuccessActions.push(
      checkoutProcessSquareCcPayment(creditCardToken, verificationToken)
    )
    dispatch(submit(NAME));
  },
  checkoutProcessStripeCCPayment: (stripePaymentMethodId: string, stripe: Stripe) => {
    submitSuccessActions.push(
      checkoutProcessStripeCCPayment(stripePaymentMethodId, stripe)
    )
    dispatch(submit(NAME));
  },
  checkoutProcessEwayCCPayment: (code: string) => {
    submitSuccessActions.push(
      checkoutProcessEwayCCPayment(code)
    )
    dispatch(submit(NAME));
  },
});

export const Container = connect<any, any, any, IshState>(
  mapStateToProps,
  mapDispatchToProps,
)(Form);

export default Container;
